/**
 * Commodity Asset Management (CAM) — RightAngle-inspired CTRM for crude,
 * refined products & biofuels. Navigation: /commodity/:section
 */
import React from "react";
import { useParams } from "react-router-dom";
import CamDashboard from "./CamDashboard";
import CamDeals from "./CamDeals";
import CamMovements from "./CamMovements";
import CamInventory from "./CamInventory";
import CamReconciliation from "./CamReconciliation";
import CamMasters from "./CamMasters";
import AiAssistant from "@/pages/epbcs/AiAssistant";

const SECTIONS = ["dashboard", "deals", "movements", "inventory", "reconciliation", "masters"];

const TITLES = {
  dashboard: "Commodity Dashboard", deals: "Trade Capture",
  movements: "Scheduling & Logistics", inventory: "Inventory Management",
  reconciliation: "Back-Office Reconciliation", masters: "Setup & Masters",
};

const SUBTITLES = {
  dashboard: "Net positions, inventory valuation and reconciliation health across your liquid commodity book.",
  deals: "Capture physical buy/sell deals for crude, refined products and biofuels — fixed or formula pricing.",
  movements: "Nominate and track pipeline, vessel, rail and truck movements against every deal.",
  inventory: "Tank & terminal stock rolled forward from receipts and deliveries, valued at market.",
  reconciliation: "Match terminal and carrier tickets to delivered volumes and clear exceptions.",
  masters: "Commodities, locations, counterparties and transport modes — the backbone of every transaction.",
};

export default function CommodityPage() {
  const { section } = useParams();
  const active = SECTIONS.includes(section) ? section : "dashboard";

  return (
    <div className="flex flex-col min-h-full bg-[#F8F9FA]">
      <div className="flex-none bg-white border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold">
              <span className="text-[#C8A24B]">Commodity Asset Management</span>
            </div>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-[#0F172A]" data-testid="cam-title">
              {TITLES[active]}
            </h1>
          </div>
          <p className="hidden xl:block max-w-md text-right text-xs leading-relaxed text-slate-500">
            {SUBTITLES[active]}
          </p>
        </div>
      </div>
      <div className="flex-1 p-4 md:p-6 w-full">
        {active === "dashboard" && <CamDashboard />}
        {active === "deals" && <CamDeals />}
        {active === "movements" && <CamMovements />}
        {active === "inventory" && <CamInventory />}
        {active === "reconciliation" && <CamReconciliation />}
        {active === "masters" && <CamMasters />}
      </div>
      <AiAssistant
        base="/cam/ai"
        title="Commodity AI Assistant"
        fabLabel="Commodity AI"
        storageKey="cam_ai_session"
        placeholder="Ask about deals, inventory, movements, recon…"
        hint="“What is my net WTI position?” · “Show reconciliation exceptions” · “Create a buy deal for 10,000 bbl WTI from Vitol” — updates always wait for your confirmation."
      />
    </div>
  );
}
